import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * "Completa el look": optional add-on below the product. Extra pieces start
 * unchecked; the shopper ticks the ones they want (choosing a size ticks the
 * piece too) and adds the checked pieces in one cart request. The button stays
 * disabled until at least one extra piece is checked. Pieces not on sale
 * unlock the look discount by tiers (data-tiers, e.g. 2 pieces −5 %, 3 −10 %,
 * 4 −15 %); the matching LOOK code is added to the cart with the pieces.
 *
 * @typedef {object} EloraLookRefs
 * @property {HTMLButtonElement} button - Adds the look to the cart
 * @property {HTMLElement} status - Polite live region for the result
 *
 * @extends Component<EloraLookRefs>
 */
class EloraLook extends Component {
  requiredRefs = ['button', 'status'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {{ min: number, percent: number, suffix: string } | undefined} */
  #tier;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('change', this.#handleChange, { signal });
    // The look lives in its own section, so listen for the main product form's variant changes on the document
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return Array.from(this.querySelectorAll('[data-look-item]'));
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    if (event.target instanceof HTMLSelectElement && event.target.value) {
      event.target.removeAttribute('aria-invalid');
      this.refs.status.textContent = '';
      // Choosing a size means the shopper wants that piece
      const check = event.target.closest('[data-look-item]')?.querySelector('.elora-look__check');
      if (check instanceof HTMLInputElement) check.checked = true;
    }
    this.#update();
  };

  /**
   * Keeps the main piece on the variant chosen in the product form.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, dialog')) return;

    try {
      await event.promise;
    } catch {
      return;
    }

    // Let the product form apply the new variant id first
    requestAnimationFrame(() => {
      const formInput = document.querySelector('product-form-component input[name="id"]');
      const main = this.querySelector('[data-main]');
      const select = main?.querySelector('[data-variant-select]');
      if (!(formInput instanceof HTMLInputElement) || !(select instanceof HTMLSelectElement)) return;

      const option = Array.from(select.options).find((opt) => opt.value === formInput.value);
      if (!option) return;

      select.value = option.value;
      const label = main?.querySelector('[data-main-variant]');
      if (label && option.dataset.label && label.textContent?.trim()) label.textContent = option.dataset.label;
      this.#update();
    });
  };

  /** @returns {{ min: number, percent: number, suffix: string }[]} Tiers from data-tiers ("min:percent:suffix,…"), lowest first */
  get #tiers() {
    return (this.dataset.tiers || '')
      .split(',')
      .map((entry) => {
        const [min, percent, suffix = ''] = entry.split(':');
        return { min: Number(min), percent: Number(percent), suffix };
      })
      .filter((tier) => tier.min > 0 && tier.percent > 0)
      .sort((a, b) => a.min - b.min);
  }

  /**
   * @param {string | undefined} template
   * @param {Record<string, string | number>} values
   */
  #fill(template, values) {
    return (template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  #update() {
    const format = this.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    const currency = this.dataset.currency || 'EUR';
    const tiers = this.dataset.lookCode ? this.#tiers : [];
    let total = 0;
    let count = 0;
    /** @type {number[]} */
    const eligiblePrices = [];
    let eligibleCount = 0;
    let extraCount = 0;

    for (const item of this.#items) {
      const check = item.querySelector('.elora-look__check');
      const select = item.querySelector('[data-variant-select]');
      const priceEl = item.querySelector('[data-item-price]');
      if (!(check instanceof HTMLInputElement) || !(select instanceof HTMLSelectElement)) continue;

      const price = Number(select.selectedOptions[0]?.dataset.price) || 0;
      if (priceEl) priceEl.textContent = formatMoney(price, format, currency);

      item.toggleAttribute('data-selected', check.checked);
      if (check.checked) {
        total += price;
        count += 1;
        if (!item.hasAttribute('data-main')) extraCount += 1;
        if (item.hasAttribute('data-eligible')) {
          eligiblePrices.push(price);
          eligibleCount += 1;
        }
      }
    }

    const totalEl = this.querySelector('[data-total]');
    const compareEl = this.querySelector('[data-total-compare]');
    const countEl = this.querySelector('[data-count]');
    // Mirrors the Shopify codes: only non-sale pieces count, and the highest tier reached applies
    const tier = tiers.filter((t) => eligibleCount >= t.min).pop();
    // Shopify rounds the percentage down on each line
    const saving = tier ? eligiblePrices.reduce((sum, price) => sum + Math.floor((price * tier.percent) / 100), 0) : 0;
    const showDiscount = saving > 0;
    const finalTotal = total - saving;
    this.#tier = showDiscount ? tier : undefined;

    if (totalEl) totalEl.textContent = formatMoney(finalTotal, format, currency);
    if (compareEl instanceof HTMLElement) {
      compareEl.hidden = !showDiscount;
      compareEl.textContent = showDiscount ? formatMoney(total, format, currency) : '';
    }
    if (countEl) countEl.textContent = count > 0 ? `(${count})` : '';

    const progressEl = this.querySelector('[data-progress]');
    if (progressEl) {
      const available = this.querySelectorAll('[data-look-item][data-eligible]').length;
      const next = tiers.find((t) => t.min > eligibleCount && t.min <= available);
      let message = '';
      if (showDiscount && tier && next && next.min === eligibleCount + 1) {
        message = this.#fill(this.dataset.progressAppliedNext, { percent: tier.percent, next: next.percent });
      } else if (showDiscount && tier) {
        message = this.#fill(this.dataset.progressApplied, { percent: tier.percent });
      } else if (next) {
        message = this.#fill(this.dataset.progressNext, { needed: next.min - eligibleCount, percent: next.percent });
      }
      progressEl.textContent = message;
    }
    this.refs.button.disabled = extraCount === 0;
  }

  /** Adds the code of the tier reached to the cart, replacing other tiers of this look and keeping other codes. */
  async #applyLookCode() {
    const base = this.dataset.lookCode;
    if (!base || !this.#tier) return;
    const code = base + this.#tier.suffix;
    const lookCodes = this.#tiers.map((t) => base + t.suffix);

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      /** @type {string[]} */
      const codes = (cart.discount_codes || []).map((/** @type {{ code: string }} */ d) => d.code);
      if (codes.includes(code) && codes.filter((c) => lookCodes.includes(c)).length === 1) return;

      const kept = codes.filter((c) => !lookCodes.includes(c));
      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: [...kept, code].join(',') }),
      });
    } catch (error) {
      // The look is still added without the discount
      console.warn('[elora-look] Could not apply the look code:', error);
    }
  }

  /** Adds every checked piece in one cart request. */
  async addLook() {
    const { button, status } = this.refs;
    const lines = [];
    /** @type {HTMLSelectElement | undefined} */
    let missingSize;

    for (const item of this.#items) {
      const check = item.querySelector('.elora-look__check');
      const select = item.querySelector('[data-variant-select]');
      if (!(check instanceof HTMLInputElement) || !check.checked || !(select instanceof HTMLSelectElement)) continue;

      if (!select.value) {
        select.setAttribute('aria-invalid', 'true');
        missingSize ??= select;
        continue;
      }
      lines.push({ id: Number(select.value), quantity: 1 });
    }

    // Every checked piece needs a size before anything is added
    if (missingSize) {
      status.textContent = this.dataset.choose || '';
      missingSize.focus();
      return;
    }
    if (lines.length === 0) return;

    button.setAttribute('aria-busy', 'true');
    status.textContent = '';

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
      .filter(Boolean);

    // Attach the look's code first so the cart rendered after the add already shows it
    await this.#applyLookCode();

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'elora-look', itemCount: 0 },
        });
        status.textContent = data.description || data.message || this.dataset.error || '';
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'elora-look',
          itemCount: lines.length,
          sections: data.sections,
          didError: false,
        },
      });
      status.textContent = this.dataset.added || '';
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      status.textContent = this.dataset.error || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('elora-look')) {
  customElements.define('elora-look', EloraLook);
}
